/**
 * Emerald MCA Solution — Unified Design System
 * Phase 5e: Comprehensive Visual System Implementation
 *
 * This system implements the official Emerald branding across all UI components.
 * Source: Brand-to-UI Spec derived from official Brandbook.
 */

/* ===================================
   TYPOGRAPHY
   =================================== */

export const TYPOGRAPHY = {
  // Font families (optimized for SaaS professional design)
  // Using system fonts as primary with strategic fallbacks
  FONT_FAMILY_HEADING: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  FONT_FAMILY_BODY: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  FONT_FAMILY: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif", // Default

  // Heading Styles (using system fonts optimized for professional display)
  H1: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: '32px',
    fontWeight: '700',
    lineHeight: '1.2',
    letterSpacing: '-1px',
  },

  H2: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: '1.2',
    letterSpacing: '-0.5px',
  },

  H3: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '1.3',
  },

  // Body Styles
  BODY_LG: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '1.5',
  },

  BODY: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '1.5',
  },

  BODY_SM: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '13px',
    fontWeight: '400',
    lineHeight: '1.5',
  },

  // Label Style
  LABEL: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '13px',
    fontWeight: '500',
    lineHeight: '1.4',
  },

  // Button Text Style
  BUTTON: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '13px',
    fontWeight: '700',
    lineHeight: '1.4',
  },

  // Caption Style
  CAPTION: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.4',
  },
};

/* ===================================
   COLORS
   =================================== */

export const COLORS = {
  // Primary Brand Colors
  PRIMARY: '#4DDB94',        // Emerald green (brand accent, CTAs)
  PRIMARY_DARK: '#115845',   // Dark green (depth, hover states)
  SECONDARY: '#000000',      // Black
  TERTIARY: '#E2E2E2',       // Light grey

  // Gradient Support Colors (intermediate stops)
  GRADIENT_DARK: '#0a0a0a',     // Darkest tone for gradients
  GRADIENT_DARK_TINT: '#051d19', // Dark gradient intermediate
  GRADIENT_MID: '#1a5a4a',      // Mid-tone gradient intermediate

  // Surface & Background Colors
  BG_APP: '#f9f9f9',         // App background (light grey, shell/sidebar)
  BG_PAGE_SURFACE: '#f6fdf9',        // Page/dashboard background (subtle Emerald tint)
  BG_SURFACE_PRIMARY: '#ffffff',      // White (cards, elevated surfaces)
  BG_SURFACE_SECONDARY: '#f6fdf9',    // Light green tint (secondary surfaces, tables)
  BG_SURFACE_TERTIARY: '#f5f5f5',     // Light grey
  BG_LIGHT: '#f6fdf9',       // Legacy: light tint background
  BG_WHITE: '#ffffff',       // Legacy: white
  BG_DARK: '#f9f9f9',        // Legacy: light grey (page bg)
  BG_FORM: '#f8f8f8',        // Light form background

  // Text Colors
  TEXT_PRIMARY: '#0a0a0a',   // Default body text, labels
  TEXT_SECONDARY: '#666666', // Lower contrast, secondary info
  TEXT_LIGHT: '#999999',     // De-emphasized, metadata
  TEXT_MUTED: '#cccccc',     // Very light, footnotes
  TEXT_WHITE: '#ffffff',     // On dark/brand backgrounds

  // Interactive States
  ACTION_PRIMARY: '#4DDB94',          // Primary CTA button
  ACTION_PRIMARY_HOVER: '#115845',    // Primary button hover
  ACTION_SECONDARY: '#ffffff',        // Secondary button
  ACTION_SECONDARY_BORDER: '#ddd',    // Secondary button border

  // Link Colors
  LINK: '#115845',
  LINK_VISITED: '#115845',
  LINK_HOVER: '#0d3b30',

  // Status Colors
  SUCCESS: '#28a745',
  WARNING: '#ffc107',
  ERROR: '#dc3545',
  INFO: '#0066cc',

  // Deal/Advance Status Badge Colors (design system tokens)
  STATUS_DRAFT_BG: '#f5f5f5',
  STATUS_DRAFT_TEXT: '#666666',
  STATUS_ACTIVE_BG: '#cfe9ff',
  STATUS_ACTIVE_TEXT: '#003a70',
  STATUS_FUNDED_BG: '#d1fae5',
  STATUS_FUNDED_TEXT: '#065f46',
  STATUS_DECLINED_BG: '#fee2e2',
  STATUS_DECLINED_TEXT: '#991b1b',
  STATUS_CLOSED_BG: '#f3f4f6',
  STATUS_CLOSED_TEXT: '#374151',

  // Border Colors
  BORDER_LIGHT: '#e5e7eb',
  BORDER_DEFAULT: '#ddd',
  BORDER_ACCENT: 'rgba(77, 219, 148, 0.15)',  // Subtle brand tint
};

/* ===================================
   SPACING SCALE
   =================================== */

export const SPACING = {
  XS: '4px',    // Minimal
  SM: '8px',    // Small
  MD: '12px',   // Default
  LG: '16px',   // Large
  XL: '24px',   // Extra large
  XXL: '32px',  // Very large
  XXXL: '48px', // Largest
};

/* ===================================
   BORDER RADIUS
   =================================== */

export const RADIUS = {
  SM: '4px',
  DEFAULT: '8px',  // Buttons, cards, inputs
  MD: '12px',      // Larger components
  LG: '14px',
  FULL: '24px',    // Pill-shaped badges
  ROUND: '50%',    // Circles
};

/* ===================================
   SHADOWS (ELEVATION SYSTEM)
   =================================== */

export const SHADOWS = {
  SUBTLE: '0 1px 2px rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 2px 4px rgba(0, 0, 0, 0.1)',      // Card default
  MD: '0 4px 8px rgba(0, 0, 0, 0.12)',          // Card hover
  LG: '0 8px 16px rgba(0, 0, 0, 0.15)',         // Elevated, modal
  ACCENT: '0 0 20px rgba(77, 219, 148, 0.25)',  // Brand glow (primary buttons)
};

/* ===================================
   PRESET STYLE OBJECTS
   =================================== */

export const STYLES = {
  // Global & Page Container
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: COLORS.BG_APP,
    padding: SPACING.LG,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  // Header / Page Title
  pageHeader: {
    maxWidth: '1400px',
    margin: '0 auto 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    gap: SPACING.LG,
  },

  pageTitle: {
    ...TYPOGRAPHY.H1,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  pageSubtitle: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_LIGHT,
    margin: `${SPACING.SM} 0 0 0`,
  },

  // CARDS
  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.LG,
    boxShadow: SHADOWS.MD,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    transition: 'all 0.2s ease',
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.XL,
    paddingBottom: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  // BUTTONS
  buttonPrimary: {
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.SM} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: TYPOGRAPHY.BUTTON.fontWeight,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: SHADOWS.ACCENT,
  },

  buttonSecondary: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.SM} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  buttonGhost: {
    backgroundColor: 'transparent',
    color: COLORS.PRIMARY_DARK,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.SM} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: '600',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  buttonSmall: {
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.XS} ${SPACING.SM}`,
    fontSize: '12px',
    fontWeight: '700',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // INPUTS & FORM
  input: {
    width: '100%',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    border: `1.5px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    boxSizing: 'border-box',
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },

  inputFocused: {
    borderColor: COLORS.PRIMARY,
    boxShadow: `0 0 0 3px rgba(77, 219, 148, 0.12)`,
  },

  formLabel: {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_PRIMARY,
    display: 'block',
    marginBottom: SPACING.SM,
  },

  // TABLES
  table: {
    maxWidth: '100%',
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    overflow: 'hidden',
    boxShadow: SHADOWS.MD,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
  },

  tableHeader: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  tableCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },

  tableRowHover: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  // BADGES & PILLS
  badge: {
    display: 'inline-block',
    padding: `${SPACING.XS} ${SPACING.MD}`,
    borderRadius: RADIUS.FULL,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '700',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  badgePrimary: {
    backgroundColor: COLORS.PRIMARY,
    color: COLORS.TEXT_WHITE,
  },

  badgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },

  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },

  badgeError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },

  // EMPTY STATES
  emptyState: {
    textAlign: 'center',
    padding: SPACING.XXXL,
    color: COLORS.TEXT_LIGHT,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  emptyStateTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_LIGHT,
    marginBottom: SPACING.MD,
  },

  emptyStateButton: {
    color: COLORS.PRIMARY,
    textDecoration: 'none',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },

  // ERROR/SUCCESS MESSAGES
  errorMessage: {
    backgroundColor: '#fee2e2',
    border: `1px solid #fca5a5`,
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    color: COLORS.ERROR,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '500',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  successMessage: {
    backgroundColor: '#dcfce7',
    border: `1px solid #86efac`,
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    color: COLORS.SUCCESS,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '500',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  warningMessage: {
    backgroundColor: '#fef3c7',
    border: `1px solid #fde047`,
    borderRadius: RADIUS.DEFAULT,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    color: '#b45309',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '500',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  // SECTION
  section: {
    marginTop: SPACING.XXL,
    paddingTop: SPACING.LG,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  sectionTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    marginBottom: SPACING.MD,
  },

  // LINKS
  link: {
    color: COLORS.LINK,
    textDecoration: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },

  linkHover: {
    color: COLORS.LINK_HOVER,
  },
};

/**
 * Helper: Merge styles
 */
export const mergeStyles = (base, override) => ({
  ...base,
  ...override,
});

/**
 * Helper: Apply hover effect
 */
export const withHover = (defaultStyle, hoverStyle) => (isHovered) => ({
  ...defaultStyle,
  ...(isHovered ? hoverStyle : {}),
});
